import React, { useEffect, useMemo, useState } from "react";
import { TableBody, TableCell, TableRow } from "../ui/table";
import { ScrollArea } from "../ui/scroll-area";
import projectXWS from "../../services/projectxWebSocket";

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeDepthLevels = (levels = [], side = null) => {
  if (!Array.isArray(levels)) {
    return [];
  }

  return levels
    .map((level) => ({
      price: toFiniteNumber(level?.price ?? level?.Price ?? level?.px ?? level?.value),
      size: Math.max(
        0,
        toFiniteNumber(
          level?.size
          ?? level?.Size
          ?? level?.quantity
          ?? level?.qty
          ?? level?.volume
          ?? (side === "bid"
            ? level?.bidSize ?? level?.BidSize ?? level?.bidQty ?? level?.BidQty ?? level?.buySize ?? level?.BuySize
            : undefined)
          ?? (side === "ask"
            ? level?.askSize ?? level?.AskSize ?? level?.askQty ?? level?.AskQty ?? level?.sellSize ?? level?.SellSize
            : undefined)
        ) || 0,
      ),
    }))
    .filter((level) => level.price && level.price > 0);
};

const normalizeDepthRows = (rows = []) => {
  if (!Array.isArray(rows)) {
    return { bids: [], asks: [] };
  }

  const bids = [];
  const asks = [];

  rows.forEach((row) => {
    const price = toFiniteNumber(row?.price ?? row?.Price ?? row?.px ?? row?.value);
    if (!price || price <= 0) {
      return;
    }

    const bidSize = Math.max(
      0,
      toFiniteNumber(
        row?.bidSize
        ?? row?.BidSize
        ?? row?.bidQty
        ?? row?.BidQty
        ?? row?.buySize
        ?? row?.BuySize
        ?? row?.bidVolume
        ?? row?.BidVolume
      ) || 0,
    );
    const askSize = Math.max(
      0,
      toFiniteNumber(
        row?.askSize
        ?? row?.AskSize
        ?? row?.askQty
        ?? row?.AskQty
        ?? row?.sellSize
        ?? row?.SellSize
        ?? row?.askVolume
        ?? row?.AskVolume
      ) || 0,
    );

    if (bidSize > 0) {
      bids.push({ price, size: bidSize });
    }
    if (askSize > 0) {
      asks.push({ price, size: askSize });
    }
  });

  return { bids, asks };
};

const extractDepthBook = (container) => {
  if (!container) {
    return { bids: [], asks: [] };
  }

  if (Array.isArray(container)) {
    return normalizeDepthRows(container);
  }

  if (typeof container !== "object") {
    return { bids: [], asks: [] };
  }

  const bidCandidates = [
    container.bids,
    container.bidLevels,
    container.buyLevels,
    container.bidDepth,
    container.bidBook,
    container.bid,
  ];
  const askCandidates = [
    container.asks,
    container.askLevels,
    container.sellLevels,
    container.askDepth,
    container.askBook,
    container.ask,
  ];

  const bids = bidCandidates.map((levels) => normalizeDepthLevels(levels, "bid")).find((levels) => levels.length) || [];
  const asks = askCandidates.map((levels) => normalizeDepthLevels(levels, "ask")).find((levels) => levels.length) || [];

  if (bids.length || asks.length) {
    return { bids, asks };
  }

  const rowCandidates = [
    container.levels,
    container.rows,
    container.book,
    container.depth,
    container.items,
    container.orderBook,
    container.marketDepth,
    container.ladder,
  ];

  return rowCandidates.map(normalizeDepthRows).find((book) => book.bids.length || book.asks.length) || { bids: [], asks: [] };
};

const normalizeDepthBook = (raw = {}) => {
  if (!raw || typeof raw !== "object") {
    return { bids: [], asks: [] };
  }

  const containers = [
    raw,
    raw.depth,
    raw.marketDepth,
    raw.orderBook,
    raw.book,
    raw.level2,
    raw.ladder,
    raw.data,
  ];

  return containers.map(extractDepthBook).find((book) => book.bids.length || book.asks.length) || { bids: [], asks: [] };
};

const PriceLadder = ({
  symbol,
  contractId,
  bidPrice = 0,
  askPrice = 0,
  currentPrice = 0,
}) => {
  const [depthBook, setDepthBook] = useState({ bids: [], asks: [], source: "awaiting" });

  useEffect(() => {
    setDepthBook({ bids: [], asks: [], source: "awaiting" });

    if (!contractId) {
      return undefined;
    }

    return projectXWS.subscribeToMarketDepth(contractId, (depth) => {
      const liveBook = depth?.book;
      if (liveBook?.bids?.length || liveBook?.asks?.length) {
        setDepthBook({ ...liveBook, source: "live-depth" });
        return;
      }

      const normalized = normalizeDepthBook(depth?.raw);
      if (normalized.bids.length || normalized.asks.length) {
        setDepthBook({ ...normalized, source: "live-depth" });
      }
    });
  }, [contractId]);

  const effectiveBids = useMemo(() => {
    if (depthBook.bids.length) {
      return depthBook.bids;
    }

    const bestBid = toFiniteNumber(bidPrice);
    if (bestBid && bestBid > 0) {
      return [{ price: bestBid, size: 0 }];
    }

    const fallbackPrice = toFiniteNumber(currentPrice);
    return fallbackPrice && fallbackPrice > 0
      ? [{ price: fallbackPrice, size: 0 }]
      : [];
  }, [bidPrice, currentPrice, depthBook.bids]);

  const effectiveAsks = useMemo(() => {
    if (depthBook.asks.length) {
      return depthBook.asks;
    }

    const bestAsk = toFiniteNumber(askPrice);
    if (bestAsk && bestAsk > 0) {
      return [{ price: bestAsk, size: 0 }];
    }

    const fallbackPrice = toFiniteNumber(currentPrice);
    return fallbackPrice && fallbackPrice > 0
      ? [{ price: fallbackPrice, size: 0 }]
      : [];
  }, [askPrice, currentPrice, depthBook.asks]);

  const rows = useMemo(() => ([
    ...[...effectiveAsks].reverse().map((row) => ({ ...row, type: "ask" })),
    ...effectiveBids.map((row) => ({ ...row, type: "bid" })),
  ]), [effectiveAsks, effectiveBids]);

  const maxTotalSize = Math.max(1, ...rows.map((row) => row.size || 0));
  const isTopOfBookFallback = depthBook.source !== "live-depth";

  return (
    <div className="rounded-lg border border-white/10 bg-black/40 overflow-hidden flex flex-col h-[300px]">
      <div className="bg-white/5 px-3 py-1.5 border-b border-white/10 flex justify-between items-center">
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/60">Depth of Market</span>
        <span className="text-[10px] font-mono text-zinc-400">{symbol}</span>
      </div>
      {isTopOfBookFallback ? (
        <div className="border-b border-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-white/60">
          Top of book
        </div>
      ) : null}
      <ScrollArea className="flex-1">
        <div className="px-1">
          <table className="w-full text-[11px] font-mono border-collapse">
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${row.type}-${row.price}-${index}`} className="hover:bg-white/5 border-none h-6 group">
                  <TableCell className="p-0 text-right pr-2 w-1/3 relative">
                    {row.type === "ask" ? (
                      <div
                        className="absolute right-0 top-0 bottom-0 bg-red-500/20"
                        style={{ width: `${row.size > 0 ? (row.size / maxTotalSize) * 100 : 0}%` }}
                      />
                    ) : null}
                    <span className={row.type === "ask" ? "text-red-400 z-10 relative" : "text-zinc-500"}>
                      {row.type === "ask" ? (row.size > 0 ? row.size : "") : ""}
                    </span>
                  </TableCell>
                  <TableCell className="p-0 text-center border-x border-white/5 w-1/3 font-bold group-hover:text-white">
                    {row.price.toFixed(2)}
                  </TableCell>
                  <TableCell className="p-0 text-left pl-2 w-1/3 relative">
                    {row.type === "bid" ? (
                      <div
                        className="absolute left-0 top-0 bottom-0 bg-green-500/20"
                        style={{ width: `${row.size > 0 ? (row.size / maxTotalSize) * 100 : 0}%` }}
                      />
                    ) : null}
                    <span className={row.type === "bid" ? "text-green-400 z-10 relative" : "text-zinc-500"}>
                      {row.type === "bid" ? (row.size > 0 ? row.size : "") : ""}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length ? (
                <TableRow className="border-none">
                  <TableCell colSpan={3} className="px-3 py-6 text-center text-xs text-white/65">
                    Awaiting broker quote
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </table>
        </div>
      </ScrollArea>
    </div>
  );
};

export default React.memo(PriceLadder);
